/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRef } from 'react';
import { Outlet, type ShouldRevalidateFunctionArgs } from 'react-router';
import type { Route } from './+types/_app';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { type ShopperProducts } from '@/scapi';
import { fetchCategoriesByIds, fetchCategory } from '@/lib/api/categories.server';
import { getLogger } from '@/lib/logger.server';
import Header from '@/components/header';
import Footer, { NEWSLETTER_SIGNUP_HEADING_CONTENT_ASSET_ID } from '@/components/footer';
import ResponsiveNavigationMenu from '@/components/navigation-menu-responsive';
import { Region } from '@/components/region';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/pageLoader';
import { fetchContentAssetBody, type ContentAssetBody } from '@/lib/api/content-asset';
import { WishlistMergeToast } from '@/components/wishlist/wishlist-merge-toast';
import { getTriplePlayEligible } from '@/lib/triple-play/triple-play.server';

type LoaderData = {
    root: Promise<ShopperProducts.schemas['Category']>;
    subs: Promise<ShopperProducts.schemas['Category'][]>;
    globalMarketingSlot: Promise<PageWithComponentData | null>;
    newsletterSignupHeading: Promise<ContentAssetBody | null>;
    /**
     * Whether the shopper is eligible for the legacy "Regular Price / Learn More"
     * Triple Play caption on PLP product tiles. Resolved server-side from the
     * `triplePlay` site preferences + the shopper's effective price zone, then
     * read on the client by `<ProductTileProvider>` via `useRouteLoaderData`.
     * The per-tile `isOnSale` check still gates each individual tile.
     */
    triplePlayEligible: boolean;
};

/**
 * URL search-params and form actions whose value drives the shopper's resolved price
 * zone. When any of these change, `_app` must revalidate so the loader recomputes
 * `triplePlayEligible` from the new zone — otherwise the legacy "Regular Price /
 * Learn More" PLP caption would lag behind a ZIP / store / region change.
 *
 * - `postal_code`, `store_code`, `region_id`, `mrkgcl` — store-resolution waterfall
 *   URL params (see `src/lib/store-resolution.ts`).
 * - `/action/update-delivery-zip` — the header / flyout ZIP widget (sets the
 *   delivery-zip cookie + Shopper Context priceZone).
 */
const ZONE_AFFECTING_SEARCH_PARAMS = ['postal_code', 'store_code', 'region_id', 'mrkgcl'] as const;
const ZONE_UPDATE_ACTION_PATH = '/action/update-delivery-zip';

/**
 * We're using the `shouldRevalidate` functionality to keep the navigation-menu category
 * fetches stable across most client-side navigations — they only need to load on first
 * mount of the `_app` layout. Re-fetching them on every page nav is the original
 * optimization this function preserves.
 *
 * The hook does, however, opt-in to revalidation when the shopper's resolved price zone
 * may have changed: a ZIP-change fetcher submission, or a URL-driven store-resolution
 * deep link (`postal_code`, `store_code`, `region_id`, `mrkgcl`). Those events flip the
 * `triplePlayEligible` baseline below; staying with `return false` would otherwise serve
 * the legacy "Regular Price / Learn More" PLP caption from the wrong zone until a hard
 * refresh.
 *
 * **Note:** If there is a desire/intent to customize that behavior further, the
 * `shouldRevalidate` function can be modified accordingly. For example, a client-side
 * storage for the categories could be introduced to implement any desired caching
 * strategy, including specific reload/refresh intervals/conditions.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
export function shouldRevalidate({ formAction, currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) {
    if (formAction === ZONE_UPDATE_ACTION_PATH) {
        return true;
    }
    for (const param of ZONE_AFFECTING_SEARCH_PARAMS) {
        if (currentUrl.searchParams.get(param) !== nextUrl.searchParams.get(param)) {
            return true;
        }
    }
    return false;
}

export function loader(args: Route.LoaderArgs): LoaderData {
    const { context } = args;
    const logger = getLogger(context);
    const config = getConfig(context);
    const { rootCategoryId, maxDepth } = config.pages.navigation;

    // `maxDepth` counts rendered menu levels; SCAPI's `levels` counts levels of
    // subcategories nested *below* the requested category, and only accepts 0, 1 or 2.
    // The root call already supplies the first rendered level, so each top-level
    // category needs `maxDepth - 1` levels beneath it. Clamping is load-bearing rather
    // than defensive: `maxDepth` is operator-configurable and the default of 3 was
    // previously passed through a bare `as 0 | 1 | 2` cast, which sent an out-of-range
    // `levels=3` that SCAPI does not accept.
    const navigationSubtreeLevels = Math.min(Math.max(maxDepth - 1, 0), 2) as 0 | 1 | 2;

    logger.debug('AppLayout: loader starting', { rootCategoryId, maxDepth, navigationSubtreeLevels });

    // Load the root category and its sub categories information.
    //
    // Attach a synchronous `.catch()` to every SCAPI promise we return to React Router. The
    // loader returns these promises (rather than awaiting them), and React Router only attaches
    // its own rejection handlers when it subscribes during rendering — there is a window
    // between promise creation and that subscription where a rejection (e.g. AuthTokenInvalidError
    // when the SLAS refresh token is invalid and the fallback guest session can't authorize)
    // would surface as an unhandled rejection and terminate the Node process. The synchronous
    // catch closes that window: we log with context and return a fallback value so the page can
    // still render (root category falling back to an empty `Category` means the nav simply shows
    // no items rather than crashing the entire app).
    //
    // Depth 1 fetches the root category with its immediate children.
    //
    // `nonPersonalized` sends `personalized=none`, which makes these responses
    // eligible for SCAPI's CDN cache so the edge answers them without contacting
    // the origin. It is scoped to the navigation fan-out below rather than applied
    // inside `fetchCategory` for every caller, because the flag disables Shopper
    // Context for the request — harmless for categories, which no hook enriches,
    // but not something to switch on globally by default. See the option's
    // docblock in `categories.server.ts` for why other endpoints must not copy it.
    //
    // It matters here specifically because the navigation is the highest-volume
    // category reader in the app: it runs on every full page render of every
    // `_app.*` route, and `cacheControlMiddleware` marks documents `no-store`, so
    // the CDN never absorbs those renders on our behalf.
    const rootCategoryPromise = fetchCategory(context, rootCategoryId, 1, { nonPersonalized: true }).catch(
        (error: unknown) => {
            logger.error('AppLayout: root category fetch failed; rendering without navigation', { error });
            return { id: rootCategoryId } as ShopperProducts.schemas['Category'];
        }
    );

    // Load each second-level sub categories tree as well, in case the resolved root-level category has any sub
    // categories and maxDepth allows for it. We then base this composed second-level promise on the initial root
    // category promise to allow for parallel loading and streaming of the two main promises.
    //
    // The whole level is read with ONE batched call. Requesting each child separately
    // put ~55 simultaneous requests on Shopper Products per server render on the Ashley
    // catalog, which exceeded SCAPI's rate limit and returned 429s — see
    // `fetchCategoriesByIds`.
    const subCategoriesPromise =
        maxDepth >= 2
            ? rootCategoryPromise
                  .then((rootCategory: ShopperProducts.schemas['Category']) => {
                      const subCategoryIds = (rootCategory.categories ?? [])
                          .filter(
                              (subCategory: ShopperProducts.schemas['Category']) =>
                                  typeof subCategory.onlineSubCategoriesCount === 'number' &&
                                  subCategory.onlineSubCategoriesCount > 0
                          )
                          .map((subCategory: ShopperProducts.schemas['Category']) => subCategory.id);

                      // Same `personalized=none` treatment as the root call above.
                      return fetchCategoriesByIds(context, subCategoryIds, navigationSubtreeLevels, {
                          nonPersonalized: true,
                      });
                  })
                  .catch((error: unknown) => {
                      logger.error('AppLayout: sub-categories fetch failed; rendering without navigation', { error });
                      return [] as ShopperProducts.schemas['Category'][];
                  })
            : Promise.resolve([] as ShopperProducts.schemas['Category'][]);

    // Site-wide marketing slot rendered under the nav on every `_app.*` route.
    // `fetchPageWithComponentData` returns `null` on 404, so an unpublished/missing
    // page renders nothing without throwing (see src/lib/util/pageLoader.ts). Any other
    // rejection (e.g. AuthTokenInvalidError) is caught here for the same reason as above —
    // the renderer already handles `null` (no marketing slot region renders).
    const globalMarketingSlotPromise = fetchPageWithComponentData(args, { pageId: 'globalmarketingslot' }).catch(
        (error: unknown) => {
            logger.error('AppLayout: globalmarketingslot fetch failed; rendering without marketing slot', { error });
            return null;
        }
    );

    // The newsletter signup heading is sourced from a Page Designer content asset. The footer is
    // present on every `_app.*` route, so a missing/unpublished asset or a transient SCAPI failure
    // must not break the entire layout. We log the failure with context (correlation ID from the
    // request-scoped logger) and resolve to `null`, which the `NewsletterSignup` component renders
    // as an i18n-based fallback heading.
    const newsletterSignupHeadingPromise = fetchContentAssetBody(
        context,
        NEWSLETTER_SIGNUP_HEADING_CONTENT_ASSET_ID
    ).catch((error: unknown) => {
        logger.warn('AppLayout: newsletter signup heading content asset unavailable; rendering fallback', {
            assetId: NEWSLETTER_SIGNUP_HEADING_CONTENT_ASSET_ID,
            error: error instanceof Error ? { message: error.message, name: error.name } : error,
        });
        return null;
    });

    const triplePlayEligible = getTriplePlayEligible(context);

    return {
        root: rootCategoryPromise,
        subs: subCategoriesPromise,
        globalMarketingSlot: globalMarketingSlotPromise,
        newsletterSignupHeading: newsletterSignupHeadingPromise,
        triplePlayEligible,
    };
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Header with navigation
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * For routes without default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({
    loaderData: { root, subs, globalMarketingSlot, newsletterSignupHeading },
}: {
    loaderData: LoaderData;
}) {
    const refRoot = useRef<Promise<ShopperProducts.schemas['Category']> | undefined>(undefined);
    const refSubs = useRef<Promise<ShopperProducts.schemas['Category'][]> | undefined>(undefined);
    if (!refRoot.current && !refSubs.current) {
        refRoot.current = root;
        refSubs.current = subs;
    }

    // INT-43 AC8: the Constructor.io behavioural-beacon `<script defer>`
    // is injected in `Layout` (`src/root.tsx`) inside the document
    // `<head>` so `defer` is honoured per the HTML spec. Keeping it
    // there also means it fires on every route (including non-`_app.*`
    // pages) the moment the dual flag (`constructorIo.beacon.enabled`
    // + `scriptUrl`) is on.

    // <WishlistMergeToast> stays at the app shell — it reads URL params and a one-time
    // cookie set by the post-login redirect target, not wishlist state. Routes that need
    // wishlist hooks mount their own <DeferredWishlistProvider> so the SCAPI call only
    // fires on pages that actually render wishlist UI.
    return (
        <>
            <WishlistMergeToast />
            {/*
             * Site-wide "globalmarketingslot" Page Designer content, rendered directly below the
             * sticky header and above the page outlet. Scrolls with the page (not the sticky nav).
             * `<Region page>` accepts the raw promise and handles its own Suspense/streaming.
             */}
            <Region page={globalMarketingSlot} regionId="content" data-testid="global-marketing-slot" />
            <ResponsiveNavigationMenu resolve={refRoot.current} defer={refSubs.current}>
                {({ mobileNavigation, desktopNavigation }) => (
                    <Header mobileNavigation={mobileNavigation} desktopNavigation={desktopNavigation} />
                )}
            </ResponsiveNavigationMenu>
            <main className="grow pt-8">
                <Outlet />
            </main>
            <Footer categories={refRoot.current} newsletterSignupHeading={newsletterSignupHeading} />
        </>
    );
}
